import { BufferGeometry, Color, Group, Object3D, Vector3 } from 'three';

import { deepCopy, isDefined } from '../../../utils/variables';
import { AttributeDataWithId } from '../../tools/utils/context-menu/design-tool/utils/attributes';
import { FaceId } from './design-node/utils/face-node';
import { LineSegmentId } from './design-node/utils/line-segment-node';
import { PointId } from './design-node/utils/point-node';

export type AttributeData =
  | {
      type: 'string';
      value: string;
      height?: number;
      rotation?: number;
    }
  | {
      type: 'number';
      value: number;
    }
  | {
      type: 'enum';
      index: ValueId;
    };

/**
 * Refers to the index in the "values" array on the SkyMap attribute.
 */
export type ValueId = number & { __type: 'ValueId' };
export type AttributeId = number & { __type: 'AttributeId' };

type AttributeBase = {
  id: AttributeId;
  name: string;
  source: 'skymap-design' | 'as-built' | 'land-xml';
  type: 'enum' | 'string' | 'number';
};

export type AttributeGroupEntry = { displayName: string; data: Map<AttributeId, AttributeData> };
export type AttributeGroups = Map<string, AttributeGroupEntry[]>;
export type AttributeGroupsJson = [string, [string, AttributeDataWithId[]][]][];

type EnumAttributeType = {
  type: 'enum';
  valueMap: Map<ValueId, string>;
  colorMap: Map<ValueId, Color>;
};

type TextAttributeType = {
  type: 'string';
  display?: boolean;
};

type NumberAttributeType = {
  type: 'number';
  unit?: SkyMapExtrasUnitType | string;
};

type AttributeType = EnumAttributeType | TextAttributeType | NumberAttributeType;

type AttributeTypeJson =
  | {
      type: 'enum';
      values: [ValueId, string][];
      colors: [ValueId, [number, number, number]][];
    }
  | TextAttributeType
  | NumberAttributeType;

export type SkyMapTextAttribute = AttributeBase & TextAttributeType;
export type SkyMapAttribute = AttributeBase & AttributeType;
export type SkyMapAttributeJson = AttributeBase & AttributeTypeJson;

export type SkyMapPositionPrimitiveExtrasJson<T extends PointId | LineSegmentId | FaceId> = {
  skymap: {
    enumAttributes: EnumValuesJson<T>[];
    numberAttributes: NumberValuesJson<T>[];
    stringAttributes: StringValuesJson<T>[];
    /**
     * The curved line segments: the control point and weight of a rational quadratic Bézier, see
     * conic.ts. Line segments that are not included are straight. Only used for line segments.
     */
    curves?: ([T, [number, number, number], number] | [T, number])[];
    /**
     * Line styles, defined in skymap-oda's shared/cpp/glb/README.md (Line styles).
     * Keyed by line segment.
     */
    widths?: [T, number][];
    stripes?: [T, StripeJson][];
    stripePatterns?: number[][];
    /**
     * The vertices where a curve is only split into pieces, e.g. the four quarter arcs of a circle
     * or an ellipse, see skymap-oda's shared/cpp/glb/README.md (Seams). With the vertex of its
     * center point in the design tool, see PointNode.seamOf. Only used for line segments.
     */
    seams?: (PointId | [PointId, PointId])[];
    /**
     * The order to draw the primitive in, for flat drawings where depth can't tell what is on top.
     */
    drawOrder?: number;
  };
};

/**
 * A curve as it is in the file, in the coordinates of the positions: a control point and weight,
 * or in older files a bulge (see arc.ts).
 */
export type CurveData = { control: Vector3; weight: number } | { bulge: number };

/**
 * P: index of the pattern, S: scale (default 1), O: offset (default 0).
 */
export type StripeJson = { P: number; S?: number; O?: number };

export type EnumValuesJson<T extends PointId | LineSegmentId | FaceId> = {
  id: AttributeId;
  valueMap: [T, ValueId][];
};

export type NumberValuesJson<T extends PointId | LineSegmentId | FaceId> = {
  id: AttributeId;
  valueMap: [T, number][];
};

/**
 * NOTE: The metadata uses minimalistic naming in order to save space when serializing into json data.
 * Since there could be hundreds or even thousands of texts in a file and they all take up valuable space.
 *
 * val = value
 * H = height
 * R = rotation
 */
export type StringValueMetadata = { val: string; H?: number; R?: number };
export type StringValuesJson<T extends PointId | LineSegmentId | FaceId> = {
  id: AttributeId;
  valueMap: [T, string | StringValueMetadata][];
};

export type ColorCoding = {
  enabled: boolean;
  attributeId?: AttributeId;
};

export function getUnitType(unit: SkyMapExtrasUnitType) {
  return unit;
}

export type SkyMapExtrasUnitType = 'none' | 'timestamp' | 'm' | 'mm' | 'kg' | 'g' | 'Ø';

/**
 * Extras are application specific metadata that may be attached to any primitive in the glb files.
 *
 * Changelog:
 * 2024-08-01 Version 2: Changes StringValuesJson metadata from string -> { val, H, R }. Both metadata types are supported for now.
 */
export type SkyMapRootNodeExtras = {
  skymap: {
    version: '1' | '2';
    colorCoding?: ColorCoding;
    attributes: SkyMapAttributeJson[];
    groups?: AttributeGroupsJson;
  };
};

export function extractSkyMapPrimitiveExtras<T extends PointId | LineSegmentId | FaceId>(
  geometry: BufferGeometry<any>,
): Map<T, Map<AttributeId, AttributeData>> {
  const attributeMap = new Map<T, Map<AttributeId, AttributeData>>();

  const extras: Partial<SkyMapPositionPrimitiveExtrasJson<T>> = geometry.userData;

  const getAttributeData = (id: T): Map<AttributeId, AttributeData> => {
    let map = attributeMap.get(id);
    if (!isDefined(map)) {
      map = new Map();
      attributeMap.set(id, map);
    }

    return map;
  };

  if (isDefined(extras) && isDefined(extras.skymap)) {
    for (const attribute of extras.skymap?.enumAttributes ?? []) {
      for (const [id, valueId] of attribute.valueMap) {
        getAttributeData(id).set(attribute.id, { type: 'enum', index: valueId });
      }
    }

    for (const attribute of extras.skymap?.numberAttributes ?? []) {
      for (const [id, value] of attribute.valueMap) {
        getAttributeData(id).set(attribute.id, { type: 'number', value });
      }
    }

    for (const attribute of extras.skymap?.stringAttributes ?? []) {
      for (const [id, value] of attribute.valueMap) {
        const isString = typeof value === 'string';
        getAttributeData(id).set(attribute.id, {
          type: 'string',
          value: isString ? value : value.val,
          height: isString ? undefined : value.H,
          rotation: isString ? undefined : value.R,
        });
      }
    }
  }

  return attributeMap;
}

/**
 * Gets the line styles of a line segments primitive.
 */
export function extractSkyMapLineStyles(geometry: BufferGeometry<any>) {
  const extras: Partial<SkyMapPositionPrimitiveExtrasJson<LineSegmentId | PointId>> =
    geometry.userData;
  const skymap = extras?.skymap;

  // Keyed by the index of the line segment.
  return {
    curves: new Map<number, CurveData>(
      (skymap?.curves ?? []).map((entry) => [
        entry[0],
        Array.isArray(entry[1])
          ? { control: new Vector3(...entry[1]), weight: entry[2]! }
          : { bulge: entry[1] },
      ]),
    ),
    widths: new Map<number, number>(skymap?.widths ?? []),
    stripes: new Map<number, { pattern: number; scale: number; offset: number }>(
      (skymap?.stripes ?? []).map(([id, stripe]) => [
        id,
        { pattern: stripe.P, scale: stripe.S ?? 1, offset: stripe.O ?? 0 },
      ]),
    ),
    patterns: skymap?.stripePatterns ?? [],
    // Keyed by the vertex, with the vertex of the center point if any.
    seams: new Map<number, number | undefined>(
      (skymap?.seams ?? []).map((entry) => (Array.isArray(entry) ? entry : [entry, undefined])),
    ),
    drawOrder: skymap?.drawOrder,
  };
}

/**
 * Whether the primitive has line styles, which are drawn with StyledLines.
 */
export function hasSkyMapLineStyles(geometry: BufferGeometry<any>) {
  const skymap = (
    geometry.userData as Partial<SkyMapPositionPrimitiveExtrasJson<LineSegmentId | PointId>>
  )?.skymap;
  return (
    isDefined(skymap) &&
    [skymap.curves, skymap.widths, skymap.stripes].some((x) => (x?.length ?? 0) > 0)
  );
}

function findSkyMapRootNodeAttributes(root: Group | Object3D) {
  let extras: Partial<SkyMapRootNodeExtras> = {};
  root.traverse((x) => {
    if (!isDefined(extras.skymap?.version)) {
      extras = x.userData;
    }
  });
  return extras;
}

/**
 * Extracts and removes the attributes from the geometry.
 * This makes modifying the data easier.
 */
export function extractSkyMapRootNodeAttributes(root: Group | Object3D): {
  attributes: Map<AttributeId, SkyMapAttribute>;
  colorCoding: ColorCoding;
  groups: AttributeGroups;
} {
  const extras = findSkyMapRootNodeAttributes(root);
  const attributes = new Map<AttributeId, SkyMapAttribute>();
  const groups: AttributeGroups = new Map();

  const valuesToValueMap = (array: [ValueId, string][]) => {
    const valueMap = new Map<ValueId, string>();
    array.forEach((x) => valueMap.set(x[0], x[1]));
    return valueMap;
  };

  const colorsToColorMap = (colors?: [ValueId, [number, number, number]][]) => {
    const valueMap = new Map<ValueId, Color>();
    (colors ?? []).forEach((x) => valueMap.set(x[0], new Color(...x[1])));
    return valueMap;
  };

  for (const attribute of extras.skymap?.attributes ?? []) {
    if (attribute.type === 'enum') {
      attributes.set(attribute.id, {
        id: attribute.id,
        source: attribute.source,
        valueMap: valuesToValueMap(attribute.values),
        name: attribute.name,
        type: 'enum',
        colorMap: colorsToColorMap(attribute.colors),
      });
    } else {
      attributes.set(attribute.id, deepCopy(attribute));
    }
  }

  for (const [name, attributes] of extras?.skymap?.groups ?? []) {
    groups.set(
      name,
      attributes.map((x) => {
        return {
          displayName: x[0],
          data: new Map(x[1].map((x) => [x.id, x])),
        };
      }),
    );
  }

  return {
    attributes,
    colorCoding: extras.skymap?.colorCoding ?? { enabled: false },
    groups,
  };
}
